import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useChatActions from 'src/components/Chat/Chat.actions';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import { firstName } from './LeadClose.functions';
import { useLeadClose } from './useLeadClose.hook';
import './LeadCloseSuggestion.styles.scss';

type tLeadCloseSuggestionProps = {
  leadId: string;
  currentStageId: string | undefined;
  currentStageName: string | undefined;
  funnelId: string | undefined;
};

/**
 * "Laura sigue en Cotización enviada. ¿La mueves a Pago confirmado?" (U14):
 * held in memory after a save from the chat; "Mover" makes the move the bar
 * makes. It goes on "Ahora no", on a chat switch, or when the lead gets there.
 */
export default function LeadCloseSuggestion(
  props: tLeadCloseSuggestionProps,
): JSX.Element | null {
  const { leadId, currentStageId, currentStageName, funnelId } = props;
  const { t } = useTranslation('chat/chat');
  const { suggestion, dismissSuggestion } = useLeadClose();
  const { updateLead } = useChatActions();
  const { setSelectedLeadForChatModal } = useModalsContext();
  const [moving, setMoving] = useState(false);
  const [failed, setFailed] = useState(false);

  const forThisLead = suggestion?.leadId === leadId;
  const arrived = forThisLead && suggestion?.stage.uuid === currentStageId;

  useEffect(() => {
    if (suggestion && (!forThisLead || arrived)) dismissSuggestion();
  }, [suggestion, forThisLead, arrived, dismissSuggestion]);

  useEffect(() => setFailed(false), [suggestion]);

  if (!suggestion || !forThisLead || arrived || !funnelId) return null;
  const { stage } = suggestion;

  async function move() {
    setMoving(true);
    setFailed(false);
    // The same request the bar's stage dropdown sends (ChatMenu.hook handleUpdateStage).
    const request = {
      stage_uuid: stage.uuid,
      funnel_uuid: funnelId,
      in_inbox: false,
    };
    const moved = await updateLead(request, { leadId });
    setMoving(false);
    if (!moved) {
      setFailed(true);
      return;
    }
    setSelectedLeadForChatModal((prev) =>
      prev && prev.id === leadId
        ? {
            ...prev,
            stage: { ...prev.stage, id: stage.uuid, name: stage.name },
          }
        : prev,
    );
    dismissSuggestion();
  }

  return (
    <div
      className="LeadCloseSuggestion"
      role="region"
      aria-label={t('leadClose.suggestion.label')}
    >
      <ArrowsRightLeftIcon className="LeadCloseSuggestion__icon" aria-hidden />
      <span className="LeadCloseSuggestion__text">
        {currentStageName ? (
          <>
            {t('leadClose.suggestion.text', {
              name: firstName(suggestion.leadName),
              stage: currentStageName,
            })}{' '}
          </>
        ) : null}
        <b>{t('leadClose.suggestion.question', { stage: stage.name })}</b>
      </span>
      <Button
        type="button"
        color="tertiary"
        size="sm"
        loading={moving}
        onClick={() => void move()}
      >
        {t('leadClose.suggestion.move')}
      </Button>
      <Button
        type="button"
        color="ghost"
        size="sm"
        disabled={moving}
        onClick={dismissSuggestion}
      >
        {t('leadClose.suggestion.dismiss')}
      </Button>
      {failed ? (
        <p className="LeadCloseSuggestion__error" role="alert">
          {t('leadClose.suggestion.failed')}
        </p>
      ) : null}
    </div>
  );
}
